/**
 * Word Error Rate calculation shared by WERDashboard and the live benchmark.
 * Word-level Levenshtein edit distance over lowercased, punctuation-stripped tokens.
 */
export function calculateWER(reference, hypothesis) {
  // Strip ALL punctuation (anything that isn't a letter/digit/space), not just ".,!?;:" -
  // needed so real parentheses/quotes/dashes a model emits don't survive to mismatch a
  // reference that had its spelled-out markers (e.g. "ouvrir la parenthèse") removed entirely.
  const normalize = (text = "") =>
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .trim()
      .split(/\s+/);

  const ref = normalize(reference);
  const hyp = normalize(hypothesis);
  const matrix = Array(ref.length + 1)
    .fill(null)
    .map(() => Array(hyp.length + 1).fill(0));

  for (let i = 0; i <= ref.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= hyp.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= ref.length; i++) {
    for (let j = 1; j <= hyp.length; j++) {
      if (ref[i - 1] === hyp[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1,
        );
      }
    }
  }

  const editDistance = matrix[ref.length][hyp.length];
  const wer = ref.length ? (editDistance / ref.length) * 100 : 0;

  return {
    wer: parseFloat(wer.toFixed(2)),
    editDistance,
    refLength: ref.length,
    hypLength: hyp.length,
  };
}

export const WER_CATEGORY_COLORS = {
  excellent: "#10b981",
  good: "#3b82f6",
  fair: "#f59e0b",
  poor: "#ef4444",
};

export function categorizeWER(wer) {
  if (wer < 5) return "excellent";
  if (wer < 10) return "good";
  if (wer < 20) return "fair";
  return "poor";
}

/**
 * French spoken-dictation formatting cues (e.g. "virgule", "point", "à la ligne") that
 * some ASR models transcribe literally and others render as real punctuation/newlines.
 * Stripping these from BOTH reference and hypothesis (symmetrically) before scoring
 * keeps WER focused on actual content-word recognition instead of punctuation-style
 * convention differences between models.
 */
const DICTATION_MARKER_PATTERNS = [
  /\bnouveau paragraphe\b/gi,
  /\bà la ligne\b/gi,
  /\ba la ligne\b/gi,
  /\bpoint d'interrogation\b/gi,
  /\bpoint d'exclamation\b/gi,
  /\bpoint[- ]virgule\b/gi,
  /\bdeux[- ]points\b/gi,
  /\bouvrir (?:les? )?(?:parenth[eè]ses?|guillemets)\b/gi,
  /\bfermer (?:les? )?(?:parenth[eè]ses?|guillemets)\b/gi,
  /\bvirgule\b/gi,
  /\bpoint\b/gi,
];

export function stripDictationMarkers(text = "") {
  return DICTATION_MARKER_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, " "),
    text,
  );
}
